// Polaris (app): the "Discard changes?" guard of the kit's EditorDrawer and EditSheet
// (src/product/components/EditorDrawer.vue, EditSheet.vue), lifted out so that every drawer asks the same way
// (decisions 5-PC-4, 8-8, 9: only when something changed; "Keep editing / Discard" inside the panel, never a second
// modal). The two drawers use it; a drawer built on the Inspector (the contact, phase 5) can use it too, with
// DiscardChangesAlert.vue for the words.
//
//   const { confirming, requestClose, runGuarded, keepEditing, discard } =
//     useDiscardGuard({ isOpen: () => open.value, isDirty: () => props.dirty, close: () => { open.value = false; }, keepId });
//
// - requestClose(): closes now when nothing changed; otherwise shows the question and closes on Discard.
// - runGuarded(action): the same for any action that would lose the edits (open another row with the drawer open).
//   Returns true when it ran at once, false when it is waiting for the answer. It guards only while the drawer is
//   open: a closed drawer whose form still differs from the original runs the action at once (no question in a
//   panel nobody sees).
// - While `confirming` is true the drawer hides its fields and shows the question; `keepId` is the id of the
//   "Keep editing" button, which takes the focus. "Keep editing" gives it back to where it was in the drawer when
//   the question came up (or to the drawer itself), so Escape keeps working (L-06).
import { nextTick, ref, watch } from 'vue';

export function useDiscardGuard({ isOpen, isDirty, close, keepId }) {
  const confirming = ref(false);
  let pending = null;
  let focusBefore = null;

  watch(isOpen, value => {
    if (value) {
      confirming.value = false;
      pending = null;
    }
  });

  const runGuarded = action => {
    if (!isOpen() || !isDirty()) {
      action();
      return true;
    }
    pending = action;
    focusBefore = document.activeElement;
    confirming.value = true;
    nextTick(() => document.getElementById(keepId)?.focus());
    return false;
  };
  const requestClose = () => runGuarded(close);
  const keepEditing = () => {
    const drawer = document
      .getElementById(keepId)
      ?.closest('[data-drawer-frame]');
    const target = drawer?.contains(focusBefore) ? focusBefore : drawer;
    confirming.value = false;
    pending = null;
    focusBefore = null;
    nextTick(() => target?.focus());
  };
  const discard = () => {
    const action = pending;
    confirming.value = false;
    pending = null;
    action?.();
  };

  return { confirming, requestClose, runGuarded, keepEditing, discard };
}
